import * as Tabs from '@radix-ui/react-tabs';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Archive, CheckCircle2, FileKey2, LoaderCircle, QrCode, ShieldCheck, XCircle } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import { useState } from 'react';
import { toast } from 'sonner';
import { api } from '../lib/api';

interface VerificationRecord {
  id: string;
  documentId: string;
  documentHash: string;
  version: string;
  status: 'PENDING' | 'VALID' | 'REVOKED';
  verificationToken: string;
  issuedAt: string | null;
  revokedAt: string | null;
  metadata: { titleAr?: string } | null;
  verificationUrl?: string;
}

interface ArchiveRecord {
  id: string;
  titleAr: string;
  storageKey: string | null;
  manifest: Record<string, unknown>;
  createdAt: string;
}

async function hashFile(file: File) {
  const digest = await crypto.subtle.digest('SHA-256', await file.arrayBuffer());
  return [...new Uint8Array(digest)].map((byte) => byte.toString(16).padStart(2, '0')).join('');
}

export function VerificationPage() {
  const queryClient = useQueryClient();
  const [selected, setSelected] = useState<VerificationRecord | null>(null);
  const [form, setForm] = useState({
    titleAr: '',
    documentId: '',
    documentHash: '',
    version: '1.0',
  });
  const recordsQuery = useQuery({
    queryKey: ['verification-records'],
    queryFn: () => api<VerificationRecord[]>('/api/verification'),
  });
  const archiveQuery = useQuery({
    queryKey: ['archive-records'],
    queryFn: () => api<ArchiveRecord[]>('/api/archive'),
  });

  const issue = useMutation({
    mutationFn: () =>
      api<VerificationRecord>('/api/verification', {
        method: 'POST',
        body: JSON.stringify(form),
      }),
    onSuccess: async (record) => {
      setSelected(record);
      toast.success('تم إصدار سجل التحقق');
      await queryClient.invalidateQueries({ queryKey: ['verification-records'] });
    },
    onError: (error) => toast.error(error.message),
  });

  const revoke = useMutation({
    mutationFn: (id: string) => api(`/api/verification/${id}/revoke`, { method: 'POST' }),
    onSuccess: async () => {
      setSelected(null);
      toast.success('تم إلغاء سجل التحقق');
      await queryClient.invalidateQueries({ queryKey: ['verification-records'] });
    },
    onError: (error) => toast.error(error.message),
  });

  async function chooseFile(file: File | undefined) {
    if (!file) return;
    const documentHash = await hashFile(file);
    setForm({
      titleAr: file.name.replace(/\.[^.]+$/, ''),
      documentId: `${file.name}-${Date.now()}`,
      documentHash,
      version: '1.0',
    });
    toast.success('حُسبت بصمة SHA-256 محليًا');
  }

  const publicUrl = selected
    ? `${window.location.origin}/verify/${selected.verificationToken}`
    : '';

  return (
    <div className="min-h-screen p-4 sm:p-8">
      <div className="mx-auto max-w-6xl">
        <header className="mb-7">
          <p className="text-xs font-black text-teal">أثر رقمي قابل للتحقق</p>
          <h1 className="mt-2 text-2xl font-black">التحقق والـQR والأرشيف</h1>
          <p className="mt-2 text-xs font-semibold text-slate-500">
            احسب البصمة محليًا، أصدر رمز تحقق، واعرض حالة المستند عبر رابط عام آمن.
          </p>
        </header>

        <Tabs.Root defaultValue="verification">
          <Tabs.List className="mb-5 flex gap-1 rounded-xl border border-ink/10 bg-white p-1">
            <Tabs.Trigger value="verification" className="inline-flex items-center gap-2 rounded-lg px-4 py-2.5 text-[11px] font-black text-slate-500 outline-none data-[state=active]:bg-ink data-[state=active]:text-white">
              <ShieldCheck className="h-3.5 w-3.5" /> سجلات التحقق
            </Tabs.Trigger>
            <Tabs.Trigger value="archive" className="inline-flex items-center gap-2 rounded-lg px-4 py-2.5 text-[11px] font-black text-slate-500 outline-none data-[state=active]:bg-ink data-[state=active]:text-white">
              <Archive className="h-3.5 w-3.5" /> الأرشيف
            </Tabs.Trigger>
          </Tabs.List>

          <Tabs.Content value="verification" className="outline-none">
            <div className="grid gap-5 xl:grid-cols-[1fr_360px]">
              <section className="space-y-4">
                <div className="rounded-2xl border border-ink/10 bg-white p-5">
                  <div className="mb-4 flex items-center gap-3">
                    <div className="grid h-10 w-10 place-items-center rounded-xl bg-teal/10 text-teal">
                      <FileKey2 className="h-4 w-4" />
                    </div>
                    <div>
                      <h2 className="text-sm font-black">إصدار تحقق لمستند</h2>
                      <p className="mt-1 text-[10px] text-slate-500">يُحسب hash داخل المتصفح ولا يحتاج رفع الملف.</p>
                    </div>
                  </div>
                  <label className="mb-4 flex cursor-pointer items-center justify-center rounded-xl border border-dashed border-teal/30 bg-teal/5 px-4 py-5 text-[11px] font-black text-teal hover:bg-teal/10">
                    اختيار ملف وحساب البصمة محليًا
                    <input type="file" className="hidden" accept=".pdf,.png,.jpg,.jpeg,.txt" onChange={(event) => void chooseFile(event.target.files?.[0])} />
                  </label>
                  <div className="grid gap-3 sm:grid-cols-2">
                    <input value={form.titleAr} onChange={(event) => setForm({ ...form, titleAr: event.target.value })} placeholder="عنوان المستند" className="rounded-xl border border-ink/10 bg-slate-50 px-3 py-2.5 text-xs outline-none focus:border-teal" />
                    <input value={form.version} onChange={(event) => setForm({ ...form, version: event.target.value })} placeholder="الإصدار" className="rounded-xl border border-ink/10 bg-slate-50 px-3 py-2.5 text-xs outline-none focus:border-teal" />
                    <input value={form.documentId} onChange={(event) => setForm({ ...form, documentId: event.target.value })} placeholder="معرف المستند" dir="ltr" className="rounded-xl border border-ink/10 bg-slate-50 px-3 py-2.5 text-xs outline-none focus:border-teal sm:col-span-2" />
                    <input value={form.documentHash} onChange={(event) => setForm({ ...form, documentHash: event.target.value.toLowerCase() })} placeholder="SHA-256 (64 hex characters)" dir="ltr" className="rounded-xl border border-ink/10 bg-slate-50 px-3 py-2.5 font-mono text-[10px] outline-none focus:border-teal sm:col-span-2" />
                  </div>
                  <button type="button" onClick={() => issue.mutate()} disabled={!form.titleAr || !form.documentId || !/^[a-f0-9]{64}$/.test(form.documentHash) || issue.isPending} className="mt-4 inline-flex items-center gap-2 rounded-xl bg-ink px-4 py-2.5 text-[10px] font-black text-white hover:bg-teal disabled:opacity-40">
                    {issue.isPending ? <LoaderCircle className="h-3.5 w-3.5 animate-spin" /> : <QrCode className="h-3.5 w-3.5" />}
                    إصدار رابط التحقق
                  </button>
                </div>

                <div className="space-y-3">
                  {recordsQuery.data?.map((record) => (
                    <article key={record.id} className="rounded-2xl border border-ink/10 bg-white p-4">
                      <div className="flex flex-wrap items-start justify-between gap-3">
                        <div>
                          <div className="flex items-center gap-2">
                            <h3 className="text-sm font-black">{record.metadata?.titleAr || record.documentId}</h3>
                            <span className={`rounded-full px-2 py-0.5 text-[8px] font-black ${
                              record.status === 'VALID' ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-700'
                            }`}>
                              {record.status === 'VALID' ? 'ساري' : 'ملغى'}
                            </span>
                          </div>
                          <p className="mt-1 text-[9px] font-semibold text-slate-400">
                            {record.documentId} · {record.version}
                          </p>
                        </div>
                        <div className="flex gap-2">
                          <button type="button" onClick={() => setSelected(record)} className="rounded-lg border border-ink/10 px-3 py-1.5 text-[9px] font-black text-teal">عرض QR</button>
                          {record.status === 'VALID' && (
                            <button type="button" onClick={() => revoke.mutate(record.id)} className="rounded-lg border border-red-100 px-3 py-1.5 text-[9px] font-black text-red-600">إلغاء</button>
                          )}
                        </div>
                      </div>
                      <code dir="ltr" className="mt-3 block truncate rounded-lg bg-slate-50 px-2 py-1.5 text-[9px] text-slate-400">
                        {record.documentHash}
                      </code>
                    </article>
                  ))}
                </div>
              </section>

              <aside className="h-fit rounded-2xl border border-ink/10 bg-white p-5 xl:sticky xl:top-5">
                <h2 className="text-sm font-black">رمز التحقق</h2>
                {selected ? (
                  <>
                    <div className="mx-auto mt-5 w-fit rounded-2xl bg-white p-4 shadow-inner ring-1 ring-slate-100">
                      <QRCodeSVG value={publicUrl} size={210} level="M" />
                    </div>
                    <p className="mt-4 break-all rounded-xl bg-slate-50 p-3 text-[9px] font-semibold text-slate-500" dir="ltr">{publicUrl}</p>
                    <button type="button" onClick={() => { void navigator.clipboard.writeText(publicUrl); toast.success('نُسخ رابط التحقق'); }} className="mt-3 w-full rounded-xl bg-teal px-3 py-2.5 text-[10px] font-black text-white">نسخ رابط التحقق</button>
                  </>
                ) : (
                  <div className="mt-12 text-center text-[11px] font-bold text-slate-400">اختر سجلًا أو أصدر تحققًا جديدًا لعرض QR.</div>
                )}
              </aside>
            </div>
          </Tabs.Content>

          <Tabs.Content value="archive" className="outline-none">
            {archiveQuery.data?.length ? (
              <div className="space-y-3">
                {archiveQuery.data.map((record) => (
                  <article key={record.id} className="rounded-2xl border border-ink/10 bg-white p-5">
                    <h2 className="text-sm font-black">{record.titleAr}</h2>
                    <p className="mt-1 text-[10px] font-semibold text-slate-400">{new Intl.DateTimeFormat('ar-SA', { dateStyle: 'medium', timeStyle: 'short' }).format(new Date(record.createdAt))}</p>
                    <p className="mt-3 text-[10px] font-semibold text-slate-500">{record.storageKey || 'لا يوجد ملف دائم — S3 غير مهيأ'}</p>
                  </article>
                ))}
              </div>
            ) : (
              <div className="rounded-2xl border border-dashed border-ink/15 py-20 text-center">
                <Archive className="mx-auto mb-3 h-7 w-7 text-teal" />
                <p className="text-sm font-black">الأرشيف فارغ</p>
                <p className="mt-1 text-[10px] font-semibold text-slate-500">ستُضاف الحزم والنتائج تلقائيًا بعد اكتمال مسارات الإخراج.</p>
              </div>
            )}
          </Tabs.Content>
        </Tabs.Root>
      </div>
    </div>
  );
}
